import React, { useCallback, useMemo} from 'react'

import { Box, Checkbox, Select, styled } from '@driftt/dds'

import { GlobalFilterCondition } from 'hooks/useAdvancedPreferences'
import useDriftAccountProperties, { AccountProperty } from 'hooks/useDriftAccountProperties'
import { FilterOperator } from 'utils/filters'

const SettingInput = styled('div', {
  marginBottom: '12px'
})

const SelectBox = styled(Box, {
  display: 'grid',
  gridTemplateColumns: "180px 108px 180px",
  columnGap: '8px',
  marginLeft: '20px',
  marginTop: '4px'
})

const operatorOptions = [
  {label: 'is', value: FilterOperator.EQUALS},
  {label: 'is not', value: FilterOperator.DOES_NOT_EQUAL}
]

type SelectOption = { label: string, value: string }

interface CustomGlobalFilterProps {
  settingIsEnabled: boolean
  setIsEnabled: (isEnabled: boolean) => void
  condition: GlobalFilterCondition
  setCondition: (condition: GlobalFilterCondition) => void
  settingLabel: string
  setShowSaveCancel: (show: boolean) => void
}

//helper to get option values for react-select 
const getOptionFromList = (optionsList: SelectOption[], value: string) => optionsList.find((option) => option.value === value)

const convertPropertiesToSelectOptions = (properties: AccountProperty[]) => {
  return properties.map(p => {
    return {
      label: p.label,
      value: p.label,
     }
  })
}

const CustomGlobalFilter = ({settingIsEnabled, setIsEnabled, condition, setCondition, settingLabel, setShowSaveCancel}: CustomGlobalFilterProps) => {
  //get drift properties and their options to populate dropdowns
  const { driftProperties } = useDriftAccountProperties()

  const getOptionsFromProperty = useCallback((property: string) => {
    const chosenProperty = driftProperties.find(p => p.label === property)
    if(chosenProperty){
      return chosenProperty.options.map(o => {
        return {
          label: o,
          value: o
        }
      })
    }
    return []
  },[driftProperties])

  //options for dropdowns
  const propertyOptions = useMemo(() => convertPropertiesToSelectOptions(driftProperties), [driftProperties])
  const valueOptions = useMemo(() => getOptionsFromProperty(condition.attribute), [getOptionsFromProperty, condition])

  if (!propertyOptions || propertyOptions.length === 0) {
    return null
  }
  
  return (
      <SettingInput>
        <Checkbox 
          label={settingLabel} 
          onChange={(checked: boolean) => {
            setIsEnabled(checked)
            setShowSaveCancel(true)
          }}
          checked={settingIsEnabled}
        />
        <SelectBox hidden={!settingIsEnabled}>
          <Select
            label='Property' 
            hideLabel={true} 
            options={propertyOptions} 
            size='fullWidth' 
            menuPortalTarget={document.getElementsByClassName('preferencesModal')[0] as HTMLElement}
            menuPosition='fixed'
            value={getOptionFromList(propertyOptions, condition.attribute)}
            onChange={(selected) => {
                // @ts-expect-error newValue in onChange is unknown
                setCondition({...condition, attribute: selected.value})
                setShowSaveCancel(true)
              }
            }
          />
          <Select 
            label='Operator' 
            hideLabel={true} 
            options={operatorOptions} 
            size='fullWidth'
            value={getOptionFromList(operatorOptions, condition.filterOperation)}
            onChange={(selected) => {
              // @ts-expect-error newValue in onChange is unknown
              setCondition({...condition, filterOperation: selected.value})
              setShowSaveCancel(true)
            }
          }
          />
          <Select 
            label='Status' 
            hideLabel={true} 
            options={valueOptions} 
            size='fullWidth' 
            menuPortalTarget={document.getElementsByClassName('preferencesModal')[0] as HTMLElement}
            menuPosition='fixed'
            value={getOptionFromList(valueOptions, condition.values ? condition.values[0] : '')}
            onChange={(selected) => {
              // @ts-expect-error newValue in onChange is unknown
              setCondition({...condition, values: [selected.value]})
              setShowSaveCancel(true)
            }}
          />
        </SelectBox>
      </SettingInput>
  )
}

export default CustomGlobalFilter
